import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from "react";
import { api } from "../../api";
import { SwitchSpinner } from "../../components/SwitchSpinner";
import { GUIDELINES_ENABLED } from "../../config";
import { Modal, Icon, Empty, Spinner, useToast } from "../../components/ui";
import { derive, raw, applyRefData } from "../admin/recordDerive";
import { workGuard, uiSwitch } from "../../helpers";
import { IssueModal } from "../../components/IssueModal";
import { GuidelinesModal } from "./GuidelinesModal";

const EMPTY_FILTER = [];   // stable reference for a tab with no column filter applied

const STATUSES_SUBMITTER = ["PA Not Required", "Approved", "Canceled", "Denied", "Reviewed", "Pending", "Withdraw"];
const STATUSES_UPLOADER = ["Approved", "Canceled", "Denied", "Reviewed"];
// statuses that require BOTH a request number and an authorization number (any submission method)
const STATUS_NEEDS_REQ_AND_AUTH = [];
// value shown in a reason dropdown for "write your own"
const OTHER_REASON = "Other (write a reason)";
const PROC_OF = { SUBMISSION: "Submitter", END_PROCESS: "Uploader", MODIFICATION: "Modifier", MORE_INFO: "More Info" };
const SHEET_NAME = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification", MORE_INFO: "More Info" };
// statuses that record the date the decision was set
const STATUS_NEEDS_DATE = ["Approved", "Denied", "Canceled", "Withdraw"];
const STATUS_DATE_REQUIRED = ["Approved", "Denied", "Canceled"]; // Withdraw = optional
const _toISO = (mdy) => { const m = String(mdy || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); return m ? `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}` : ""; };
const _fromISO = (iso) => { const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? `${m[2]}/${m[3]}/${m[1]}` : ""; };
const _todayMDY = () => { const d = new Date(); return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`; };
const SUBMIT_METHODS = ["Portal", "Call", "Fax"];

// Reason dropdown values per status (Remarks stays free text)
const REASON_OPTIONS = {
  Canceled: ["Duplicate request", "Issue with health plan"],
  Denied: ["Not medically necessary", "Criteria not met", "Lack of supporting documentation"],
  Reviewed: ["MD notes unsigned / Latest notes not uploaded", "No response from task", "Future dates"],
  Pending: ["Awaiting payor response", "Submitted – under review"],
  Withdraw: ["Provider withdraw", "Patient withdraw"],
};

function inboxOptions(status, isUploader) {
  if (isUploader) {
    switch (status) {
      case "Approved": return ["Ready to Schedule: Urgent", "Ready to Schedule: Routine"];
      case "Canceled": return ["Canceled", "Modification Needed", "More Info Needed"];
      case "Denied": return ["Denied", "More Info Needed", "Modification Needed"];
      case "Reviewed": return ["Request Pending"];
      default: return [];
    }
  }
  switch (status) {
    case "Approved":
    case "PA Not Required": return ["Ready to Schedule: Urgent", "Ready to Schedule: Routine"];
    case "Reviewed": return [];                    // auto from the sheet, not editable
    case "Denied": return ["Denied", "Modification Needed", "More Info Needed"];
    case "Pending": return ["Request Pending"];
    case "Canceled": return ["Canceled"];
    case "Withdraw": return ["Withdrawl"];
    default: return [];
  }
}

const CPT_RE = /^(\d{5}|\d{4}[A-Za-z]|[A-Za-z]\d{4})$/;
const ICD_RE = /^[A-Za-z]\d[A-Za-z0-9](\.[A-Za-z0-9]{1,4})?$/;
const splitCodes = (s) => (s ? s.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean) : []);

const PROCESS_TO_SHEET = { SUBMITTER: "SUBMISSION", UPLOADER: "END_PROCESS", MODIFIER: "MODIFICATION", MORE_INFO: "MORE_INFO" };

// Header-filter dropdown for the user grid (Portal). Client-side, multi-select. Rendered with position:fixed
// anchored to the header cell (so the grid's overflow never clips it) and WITHOUT a backdrop (so it never
// blocks page/grid scrolling). It re-anchors as the page/grid scrolls, and closes on an outside click or Escape.
function ColMenu({ anchorRef, options, selected, label, numeric, onApply, onClose }) {
  const [sel, setSel] = useState(selected || []);
  const [term, setTerm] = useState("");
  const [pos, setPos] = useState(null);
  const boxRef = useRef(null);
  useEffect(() => {
    const place = () => {
      const el = anchorRef && anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const width = 250;
      setPos({ top: Math.round(r.bottom + 6), left: Math.round(Math.min(r.left, window.innerWidth - width - 12)) });
    };
    place();
    window.addEventListener("scroll", place, true);   // capture=true → also fires for the inner grid scroll
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [anchorRef]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target) && anchorRef && anchorRef.current && !anchorRef.current.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc, true);
    document.addEventListener("keydown", onKey, true);
    return () => { document.removeEventListener("mousedown", onDoc, true); document.removeEventListener("keydown", onKey, true); };
  }, [onClose, anchorRef]);
  const toggle = (v) => setSel((s) => (s.includes(v) ? s.filter((x) => x !== v) : [...s, v]));
  const shown = options.filter((o) => !term.trim() || String(o).toLowerCase().includes(term.trim().toLowerCase()));
  return (
    <div ref={boxRef} onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: pos ? pos.top : -9999, left: pos ? pos.left : -9999, zIndex: 1000, background: "#fff", border: "1px solid #d7dee8", borderRadius: 12, boxShadow: "0 12px 32px rgba(15,23,42,.18)", width: 250, padding: 12, fontWeight: 500, color: "#0f172a", cursor: "default", textTransform: "none", letterSpacing: "normal" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: "#0f172a" }}>{label}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#4f46e5", cursor: "pointer" }} onClick={() => setSel(sel.length === options.length ? [] : [...options])}>{sel.length === options.length && options.length ? "Clear all" : "Select all"}</span>
        </div>
        <input autoFocus value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search…"
          style={{ width: "100%", boxSizing: "border-box", border: "1px solid #d7dee8", borderRadius: 8, padding: "7px 10px", fontSize: 13, color: "#0f172a", marginBottom: 8, outline: "none" }} />
        <div style={{ maxHeight: 240, overflow: "auto", margin: "0 -4px" }}>
          {shown.length === 0 && <div style={{ fontSize: 12.5, color: "#64748b", padding: "8px 6px" }}>No values</div>}
          {shown.map((o) => {
            const on = sel.includes(o);
            return (
              <label key={o} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 8px", fontSize: 13.5, color: "#0f172a", cursor: "pointer", borderRadius: 8, background: on ? "#eef2ff" : "transparent" }}>
                <input type="checkbox" checked={on} onChange={() => toggle(o)} style={{ width: 16, height: 16, accentColor: "#4f46e5", cursor: "pointer" }} />
                <span style={{ color: "#0f172a", fontWeight: on ? 700 : 500 }}>{numeric ? `RTC ${o}` : o}</span>
              </label>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, borderTop: "1px solid #eef2f7", paddingTop: 10 }}>
          <button className="btn btn-primary btn-sm" style={{ flex: 1 }} onClick={() => onApply(sel)}>Apply{sel.length ? ` (${sel.length})` : ""}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setSel([]); onApply([]); }}>Clear</button>
        </div>
    </div>
  );
}

// The most recent worker on a record who ISN'T the current user. Non-empty only when the record was worked
// and kept by SOMEONE ELSE (i.e. reassigned to me) — so a user's own freshly-kept records show nothing.
// workHistory keeps every worker, so this survives re-assignment between users.
function prevOtherWorker(r, meName) {
  const me = (meName || "").trim().toLowerCase();
  try {
    const hist = r.workHistory ? JSON.parse(r.workHistory) : [];
    for (let i = hist.length - 1; i >= 0; i--) {
      const by = hist[i] && (hist[i].by || hist[i].byName);
      if (by && String(by).trim().toLowerCase() !== me) return String(by);
    }
  } catch { /* ignore malformed history */ }
  const wb = r.workedByName || "";
  return (wb && wb.trim().toLowerCase() !== me) ? wb : "";
}

export function AssignedRecords({ user, process, client, processes }) {
  // Must match the name stored in work_history ("by"), which is the user's full name (first + last).
  const meName = (user && (user.fullName || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.casName || user.name || user.username)) || "";
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(null);
  const gridScrollRef = useRef(null);   // the scrollable grid container
  const savedScrollRef = useRef(0);     // last scroll position — restored after opening/closing a record
  // After the list re-renders (record closed), put the scroll back where it was instead of jumping to top.
  useLayoutEffect(() => { if (!working && gridScrollRef.current) gridScrollRef.current.scrollTop = savedScrollRef.current; }, [working]);
  const [tab, setTab] = useState(null);
  // App renders the one centered overlay (uiSwitch); this view no longer paints its own on tab switch
  // (that was the second, offset spinner). Firing uiSwitch synchronously keeps the instant-feedback.
  // Filters stay applied across tab switches — a filter the user set persists until they clear it
  // themselves. (Only the open filter dropdown is closed on switch.)
  const switchTab = (k) => { if (activeTab === k) return; uiSwitch.trigger(); setTab(k); setOpenCol(null); };
  const [refReady, setRefReady] = useState(0);   // bumped once payor→portal mapping is loaded
  // Column filters are kept PER TAB (keyed by the active tab): filtering Process does not affect Reviewed
  // or any other tab, and each tab's own selection persists until the user clears it there. The derived
  // `portalFilter`/`rtcFilter` for the current tab and their setters are defined just below activeTab.
  const [portalByTab, setPortalByTab] = useState({});
  const [rtcByTab, setRtcByTab] = useState({});
  const [openCol, setOpenCol] = useState(null);           // which header filter dropdown is open
  const [todayRecs, setTodayRecs] = useState(null);   // constant, log-based "assigned today" set (for the count badge)
  const load = useCallback(async (silent) => {
    setErr("");
    try { setRows(await api.listAssignedRecords(silent ? { silent: true } : undefined)); }
    catch (e) { setErr(e.message); if (!silent) setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // load the payor→portal mapping so the Portal column derives correctly (same as the admin grid)
  useEffect(() => { api.getRefData().then((ref) => { applyRefData(ref); setRefReady((n) => n + 1); }).catch(() => {}); }, []);
  // "Assigned today" count — from the immutable assignment log, so it stays CONSTANT for the whole day (doesn't drop
  // when a record is worked, set to Pending, kept in Reviewed, or routed on). Merged across the user's processes.
  const procKey = (processes || []).join(",");
  const loadToday = useCallback(async () => {
    const sheets = (processes || []).map((p) => PROCESS_TO_SHEET[p]).filter(Boolean);
    const use = sheets.length ? sheets : ["SUBMISSION", "END_PROCESS", "MODIFICATION", "MORE_INFO"];
    try {
      const lists = await Promise.all(use.map((s) => api.dayDetail({ sheet: s }).catch(() => [])));
      const m = new Map();
      lists.flat().forEach((r) => { if (r && r.id != null) m.set(r.id, r); });
      setTodayRecs([...m.values()]);
    } catch { setTodayRecs([]); }
  }, [procKey]); // eslint-disable-line
  useEffect(() => { loadToday(); }, [loadToday, rows]);

  const SHEET_TABS = [["SUBMISSION", "Submission"], ["END_PROCESS", "End Process"], ["MODIFICATION", "Modification"], ["MORE_INFO", "More Info"]];
  // Sheets the user handles (from their processes) + any sheet that has records assigned to them.
  const myProcSheets = new Set((processes || []).map((p) => PROCESS_TO_SHEET[p]).filter(Boolean));
  // Show tabs ONLY for the processes this user actually holds. (Fall back to sheets-with-records
  // only if the account has no processes configured, so records are never hidden by mistake.)
  const sheetsPresent = myProcSheets.size
    ? SHEET_TABS.filter(([k]) => myProcSheets.has(k))
    : SHEET_TABS.filter(([k]) => (rows || []).some((r) => r.sheet === k));
  // NOTE: declared here (before inGroup / reviewedCount use it) so it's never read in the temporal dead zone.
  const derived = useMemo(() => { const m = new Map(); (rows || []).forEach((r) => m.set(r.id, derive(r))); return m; }, [rows, refReady]);
  const inGroup = (r) => { if (!client) return true; const g = r.productionGroup || (derived.get(r.id) || {}).location; return g === client; };
  const bizDay = (t) => { try { return new Date(new Date(t).getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10); } catch { return ""; } };   // 8:30 IST business day
  const todayBiz = bizDay(Date.now());
  const isNewToday = (r) => r.assignedAt && bizDay(r.assignedAt) === todayBiz;   // assigned to them today (this business day)
  const isReviewedKept = (r) => r.paStatus === "Reviewed" || !!r.reviewedSince;   // records kept in Reviewed
  // Calendar days a record has been kept in Reviewed (kept today = 0, next calendar day = 1, …).
  const daysKept = (r) => {
    const t = r.reviewedSince || r.assignedAt; if (!t) return null;
    const a = new Date(t), b = new Date();
    const da = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const db = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.max(0, Math.round((db - da) / 86400000));
  };
  // Future date the user set when the reason to keep in Reviewed is "future date". Blank/invalid -> "".
  const fmtFuture = (v) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  };
  // Reviewed-kept records live ONLY in the Reviewed tab — keep them out of the process tab counts.
  const countFor = (k) => (rows || []).filter((r) => r.sheet === k && inGroup(r) && !isReviewedKept(r)).length;
  const reviewedCount = (rows || []).filter((r) => inGroup(r) && isReviewedKept(r)).length;
  const activeTab = tab === "__REVIEWED__" ? "__REVIEWED__" : ((tab && sheetsPresent.some(([k]) => k === tab)) ? tab : (sheetsPresent[0] ? sheetsPresent[0][0] : null));
  // Per-tab column filters for the tab currently shown. Applying/clearing a filter only touches this tab.
  const portalFilter = portalByTab[activeTab] || EMPTY_FILTER;
  const rtcFilter = rtcByTab[activeTab] || EMPTY_FILTER;
  const setPortalFilter = (v) => setPortalByTab((m) => ({ ...m, [activeTab]: (typeof v === "function" ? v(m[activeTab] || []) : v) }));
  const setRtcFilter = (v) => setRtcByTab((m) => ({ ...m, [activeTab]: (typeof v === "function" ? v(m[activeTab] || []) : v) }));

  const prio = (r) => { const d = derived.get(r.id) || {}; return (d.clinicPriority === "STAT" ? 2 : 0) + (d.urgentRoutine === "Urgent" ? 1 : 0); };
  // rows in the current tab (for building the Portal / RTC filter option lists)
  // Build the Portal/RTC filter option lists from exactly the rows the tab actually shows. Reviewed-kept
  // records live only under the Reviewed tab, so they must be excluded on the process tabs here too — else
  // an RTC band that exists only in Reviewed (e.g. RTC 6) would appear as a Process-tab filter option and
  // match nothing when selected. Mirrors the tab logic in `filtered` below.
  const tabRows = useMemo(() => (rows || []).filter((r) => inGroup(r) && (activeTab === "__REVIEWED__" ? isReviewedKept(r) : ((!activeTab || r.sheet === activeTab) && !isReviewedKept(r)))), [rows, activeTab, client, refReady]); // eslint-disable-line
  const portalOpts = useMemo(() => [...new Set(tabRows.map((r) => (derived.get(r.id) || {}).portal).filter((v) => v != null && v !== ""))].sort((a, b) => String(a).localeCompare(String(b))), [tabRows, derived]);
  const portalHeadRef = useRef(null);
  const rtcHeadRef = useRef(null);
  // RTC priority bands present in the current tab — numeric order (low → high), blanks excluded (shown as "—").
  const rtcOpts = useMemo(() => [...new Set(tabRows.map((r) => (derived.get(r.id) || {}).rtcPriority).filter((v) => v != null && v !== ""))]
    .map((v) => String(v))
    .sort((a, b) => { const na = Number(a), nb = Number(b); return (Number.isNaN(na) || Number.isNaN(nb)) ? a.localeCompare(b) : na - nb; }), [tabRows, derived]);
  const filtered = (rows || []).filter((r) => {
    if (activeTab === "__REVIEWED__") { if (!isReviewedKept(r)) return false; }
    else { if (activeTab && r.sheet !== activeTab) return false; if (isReviewedKept(r)) return false; }  // reviewed-kept show only under Reviewed
    if (client) { const g = r.productionGroup || (derived.get(r.id) || {}).location; if (g !== client) return false; }
    const d = derived.get(r.id) || {};
    if (portalFilter.length && !portalFilter.includes(d.portal || "")) return false;
    if (rtcFilter.length && !rtcFilter.includes(d.rtcPriority == null || d.rtcPriority === "" ? "" : String(d.rtcPriority))) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [r.mrn, d.orders, d.clinic].some((v) => (v || "").toString().toLowerCase().includes(s));
  }).sort((a, b) => prio(b) - prio(a));

  // NOTE: kept-in-Reviewed records are NOT auto-opened on login. The user opens them from the Reviewed tab
  // themselves (the red "Check N kept in Reviewed" reminder points them there). Once a record IS opened, a
  // remark is still required before leaving (the X / Back / tab switch / logout / tab-close guards remain).
  if (working) {
    return <WorkView key={working.id} record={working} role={user.role} user={user}
      onClose={() => { setWorking(null); load(true); }}
      onSaved={() => { setWorking(null); load(true); }}
      onOpenNew={(rec) => setWorking(rec)} />;
  }

  // ── Summary stats for the side rail ──
  const inView = (rows || []).filter(inGroup);
  const statIds = new Set(inView.filter((r) => { const dd = derived.get(r.id) || {}; return r.priorityOverride === "STAT" || dd.clinicPriority === "STAT"; }).map((r) => r.id));
  const statCount = statIds.size;
  const urgentCount = inView.filter((r) => (derived.get(r.id) || {}).urgentRoutine === "Urgent").length;
  const todayCount = todayRecs ? todayRecs.length : 0;
  const statToday = (todayRecs || []).filter((r) => statIds.has(r.id)).length;
  const toWork = inView.filter((r) => !isReviewedKept(r) && !(r.paStatus && String(r.paStatus).trim())).length;
  const reviewNeed = inView.filter((r) => isReviewedKept(r) && !r.reviewCheckedToday && !(r.reviewedSince && bizDay(r.reviewedSince) === todayBiz)).length;

  return (
    <div className="uv-layout">
      <div className="uv-main">
      {/* Compact control line: search · tabs · reviewed reminder */}
      <div className="ug-toolbar">
        <div className="search ug-search"><Icon.search /><input className="inp" placeholder="Search PID, order, clinic…" value={q} onChange={(e) => setQ(e.target.value)} /></div>

        {(sheetsPresent.length > 1 || reviewedCount > 0) && (
          <div className="seg-tabs">
            {sheetsPresent.map(([k, label]) => (
              <button key={k} className={"seg-tab" + (activeTab === k ? " on" : "")} onClick={() => switchTab(k)}>
                {label}<span className="seg-tab-n">{countFor(k)}</span>
              </button>
            ))}
            {reviewedCount > 0 && (
              <button className={"seg-tab rev" + (activeTab === "__REVIEWED__" ? " on" : "")} onClick={() => switchTab("__REVIEWED__")}>
                Reviewed<span className="seg-tab-n">{reviewedCount}</span>
              </button>
            )}
          </div>
        )}

        {reviewedCount > 0 && (
          <span className="ug-alert" title="Open the Reviewed tab and add today's note for each kept record">
            <span className="ug-alert-dot" />Check <b>{reviewedCount}</b> kept in Reviewed
          </span>
        )}
      </div>

      <div className="card uv-grid-card" style={{ overflow: "hidden" }}><div ref={gridScrollRef} onScroll={(e) => { savedScrollRef.current = e.currentTarget.scrollTop; }} className="table-wrap grid-scroll" style={{ overflowY: "auto", overflowX: "hidden" }}>
        <table className={"tbl select-tbl user-grid" + (activeTab === "__REVIEWED__" ? " rev-grid" : "")}>
          <colgroup>
            {activeTab === "__REVIEWED__" ? (
              <>
                <col style={{ width: "3%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "7%" }} />{/* Urgent/Routine */}
                <col style={{ width: "10%" }} />{/* Portal widened */}
                <col style={{ width: "5%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "11%" }} />{/* Reason kept — now beside Latest Remark */}
                <col style={{ width: "10%" }} />{/* Latest Remark */}
                <col style={{ width: "12%" }} />{/* UID — full value, word-wrapped */}
              </>
            ) : (
              <>
                <col style={{ width: "3%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "13%" }} />{/* Order */}
                <col style={{ width: "6%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "8%" }} />
                <col style={{ width: "11%" }} />{/* Portal widened */}
                <col style={{ width: "6%" }} />
                <col style={{ width: "17%" }} />{/* UID — widened in process */}
              </>
            )}
          </colgroup>
          <thead><tr>
            <th style={{ textAlign: "center" }}>#</th>
            <th>PID (MRN)</th>
            {activeTab !== "__REVIEWED__" && <th>Order</th>}
            {activeTab !== "__REVIEWED__" && <th>Group</th>}
            <th>Clinic Location</th>
            <th>Order Date</th>
            {activeTab !== "__REVIEWED__" && <th>Assigned On</th>}
            <th style={{ paddingTop: 2, paddingBottom: 2 }}><span style={{ fontSize: "8.5px", lineHeight: 1.08, display: "inline-block", whiteSpace: "normal", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".02em" }}>Urgent /<br />Routine</span></th>
            <th ref={portalHeadRef} style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setOpenCol(openCol === "portal" ? null : "portal"); }}>
              Portal <span style={{ fontSize: 11, opacity: portalFilter.length ? 1 : 0.55 }}>▾</span>{portalFilter.length ? <span style={{ marginLeft: 4, background: "#4f46e5", color: "#fff", borderRadius: 999, padding: "0 6px", fontSize: 10.5, fontWeight: 800 }}>{portalFilter.length}</span> : null}
              {openCol === "portal" && <ColMenu anchorRef={portalHeadRef} options={portalOpts} selected={portalFilter} label="Filter portal" onApply={(s) => { setPortalFilter(s); setOpenCol(null); }} onClose={() => setOpenCol(null)} />}
            </th>
            <th ref={rtcHeadRef} style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); setOpenCol(openCol === "rtc" ? null : "rtc"); }}>
              RTC <span style={{ fontSize: 11, opacity: rtcFilter.length ? 1 : 0.55 }}>▾</span>{rtcFilter.length ? <span style={{ marginLeft: 4, background: "#4f46e5", color: "#fff", borderRadius: 999, padding: "0 6px", fontSize: 10.5, fontWeight: 800 }}>{rtcFilter.length}</span> : null}
              {openCol === "rtc" && <ColMenu anchorRef={rtcHeadRef} options={rtcOpts} selected={rtcFilter} label="Filter RTC priority" numeric onApply={(s) => { setRtcFilter(s); setOpenCol(null); }} onClose={() => setOpenCol(null)} />}
            </th>
            {activeTab === "__REVIEWED__" && <th>Days in Reviewed</th>}
            {activeTab === "__REVIEWED__" && <th>Future Date</th>}
            {activeTab === "__REVIEWED__" && <th>Reason kept</th>}
            {activeTab === "__REVIEWED__" && <th>Latest Remark</th>}
            <th>UID</th>
          </tr></thead>
          <tbody>
            {filtered.map((r, i) => {
              const d = derived.get(r.id) || {};
              const isRev = r.paStatus === "Reviewed" || !!r.reviewedSince;
              // In the Reviewed tab: MRN turns GREEN once the user has opened it and left today's remark; otherwise it
              // stays DARK RED so, each day, records they haven't touched yet stand out. (Resets daily — reviewCheckedToday
              // is computed per business day on the server, so no separate job is needed.)
              // GREEN once it's been handled today — either kept in Reviewed today (fresh) or today's note was added.
              // Otherwise RED (kept on an earlier day, still needs today's note). Solid chips stay readable on the
              // yellow reviewed rows.
              const keptToday = r.reviewedSince && bizDay(r.reviewedSince) === todayBiz;
              const revDone = isRev && (r.reviewCheckedToday || keptToday);
              const revChip = activeTab === "__REVIEWED__" && isRev;
              // Previous worker — set ONLY when the record was kept by another user and reassigned to me
              // (self-kept records show nothing). Shown as plain text + a row accent so it stands out.
              const pw = revChip ? prevOtherWorker(r, meName) : "";
              const lastRemark = r.reviewLastComment ? String(r.reviewLastComment) : "";
              const mrnTitle = revChip
                ? (revDone
                    ? (lastRemark ? `Today's note: ${lastRemark}` : "Kept in Reviewed today")
                    : (lastRemark ? `Previous note: ${lastRemark}` : "Needs today's note — open to add it"))
                : undefined;
              const rowTitle = isRev
                ? (lastRemark ? `Latest remark: ${lastRemark}` : "Kept in Review — open to add today's remark")
                : "Double-click to open";
              const isStat = r.priorityOverride === "STAT" || d.clinicPriority === "STAT";
              return (
                <tr key={r.id} className={"row-click" + (revChip ? (revDone ? " rev-row-ok" : " rev-row-need") : "")} onDoubleClick={() => setWorking(r)} title={rowTitle}>
                  <td style={{ textAlign: "center", color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>{i + 1}</td>
                  <td className="pid-cell">
                    <span className="pid-wrap">
                      {isNewToday(r) && !(revChip && keptToday) && <span className="pid-newdot" title="Assigned to you today" />}
                      <span className={"mono pid-mrn" + (revChip ? (revDone ? " rev-ok" : " rev-need") : "")} title={mrnTitle}>{r.mrn}</span>
                      {isStat && <span className={"pid-stat" + (r.priorityOverride === "STAT" ? " is-m" : "")} title={r.priorityOverride === "STAT" ? "Manually made STAT (was Non-STAT) — work this first" : "STAT priority"}>{r.priorityOverride === "STAT" ? "M·STAT" : "STAT"}</span>}
                    </span>
                    {pw && <div style={{ marginTop: 3, fontSize: 10, color: "#6d28d9", fontWeight: 600, whiteSpace: "normal", lineHeight: 1.15, wordBreak: "break-word" }} title={"Previously worked by " + pw}>Prev: {pw}</div>}
                  </td>
                  {activeTab !== "__REVIEWED__" && <td className="wrap">{d.orders}</td>}
                  {activeTab !== "__REVIEWED__" && <td>{r.productionGroup || d.location || "—"}</td>}
                  <td className="cell-sub wrap">{d.clinic || "—"}</td>
                  <td className="cell-sub">{d.orderDate || "—"}</td>
                  {activeTab !== "__REVIEWED__" && <td style={{ whiteSpace: "nowrap" }} className="cell-sub">{r.assignedAt ? new Date(r.assignedAt).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "—"}</td>}
                  <td>{d.urgentRoutine === "Urgent" ? <span className="pill-urgent">Urgent</span> : <span className="cell-sub">Routine</span>}</td>
                  <td className="cell-sub wrap" title={d.portal || ""}>{d.portal || "—"}</td>
                  <td>{d.rtcPriority === "" || d.rtcPriority == null ? "—" : d.rtcPriority}</td>
                  {activeTab === "__REVIEWED__" && (() => { const dk = daysKept(r); const strong = dk != null && dk >= 3; return (
                    <td style={{ whiteSpace: "nowrap" }}>{dk == null ? "—" : <b style={{ color: strong ? "#dc2626" : "#4f46e5", fontWeight: strong ? 800 : 700 }}>{dk === 0 ? "Today" : `${dk} day${dk === 1 ? "" : "s"}`}</b>}</td>
                  ); })()}
                  {activeTab === "__REVIEWED__" && (() => { const fd = fmtFuture(r.reviewFutureDate); return (
                    <td style={{ whiteSpace: "nowrap" }} className="cell-sub">{fd || "—"}</td>
                  ); })()}
                  {activeTab === "__REVIEWED__" && <td className="cell-sub wrap" title={r.reason || ""}>{r.reason || "—"}</td>}
                  {activeTab === "__REVIEWED__" && <td className="cell-sub wrap" title={lastRemark || ""}>{lastRemark || "—"}</td>}
                  <td className="mono cell-sub uid-cell" title={r.uniqueId}>{r.uniqueId}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows === null && <div className="empty"><Spinner /></div>}
        {err && <div className="err-text" style={{ padding: 18 }}>{err}</div>}
        {rows && filtered.length === 0 && !err && <Empty icon={<Icon.clipboard width="40" height="40" />} title="Nothing assigned yet" text="Records assigned to you will appear here." />}
      </div></div>
      </div>

      <aside className="uv-rail">
        <div className="summary-board">
          <div className="summary-title"><Icon.home width="16" height="16" />My summary</div>
          <div className="sum-chips">
            <span className="sum-chip sum-chip-stat">STAT <b>{statCount}</b></span>
            <span className="sum-chip sum-chip-urg">Urgent <b>{urgentCount}</b></span>
          </div>
          <div className="uv-tiles">
            <div className="uv-tile uv-tile-today"><span className="uv-tile-n">{todayCount}</span><span className="uv-tile-l">Assigned today</span></div>
            <div className="uv-tile"><span className="uv-tile-n">{statToday}</span><span className="uv-tile-l">STAT today</span></div>
            <div className="uv-tile" title="Records assigned to you that you haven't worked yet"><span className="uv-tile-n">{toWork}</span><span className="uv-tile-l">To do</span></div>
            <div className={"uv-tile" + (reviewNeed > 0 ? " uv-tile-warn" : "")}><span className="uv-tile-n">{reviewNeed}</span><span className="uv-tile-l">Reviewed to check</span></div>
          </div>
          <div className="uv-proc">
            <div className="uv-proc-h">By process</div>
            {sheetsPresent.map(([k, label]) => (
              <div className={"uv-proc-row" + (activeTab === k ? " on" : "")} key={k}><span>{label}</span><b>{countFor(k)}</b></div>
            ))}
            {reviewedCount > 0 && <div className={"uv-proc-row" + (activeTab === "__REVIEWED__" ? " on" : "")}><span>Reviewed</span><b>{reviewedCount}</b></div>}
            <div className="uv-proc-row uv-proc-total"><span>Total</span><b>{inView.length}</b></div>
          </div>
        </div>
      </aside>
    </div>
  );
}

function CodeChips({ label, codes, setCodes, validate, placeholder, hint, onDirty }) {
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState("");
  const add = () => {
    const v = draft.trim().toUpperCase();
    if (!v) return;
    if (!validate(v)) { setErr(`“${v}” isn't a valid ${label} code.`); return; }
    if (!codes.includes(v)) { setCodes([...codes, v]); onDirty && onDirty(); }
    setDraft(""); setErr("");
  };
  const onKey = (e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } };
  return (
    <div className="field">
      <label>{label}</label>
      <div className="chip-input">
        {codes.map((c) => (
          <span key={c} className="code-chip">{c}<button type="button" onClick={() => { setCodes(codes.filter((x) => x !== c)); onDirty && onDirty(); }}>×</button></span>
        ))}
        <input value={draft} onChange={(e) => { setDraft(e.target.value); setErr(""); }} onKeyDown={onKey} onBlur={add} placeholder={placeholder} />
      </div>
      {err ? <div className="code-err">{err}</div> : hint ? <div className="hint" style={{ marginTop: 4 }}>{hint}</div> : null}
    </div>
  );
}

/* ---------------- Full-page work view ---------------- */
export const SUBMITTER_GROUPS = ["PHS", "LACN", "CCCMG"];
// LACN has a fixed list; PHS/CCCMG accept the CAS name typed in (live PHS CAS list can be wired later).
const SUBMITTER_NAMES = {
  LACN: ["Debbie Ketabchi", "Diana Ko", "Maria Hernandez", "Susie Salgado"],
};

export function WorkView({ record, onClose, onSaved, onOpenNew, role, user }) {
  const isSupervisor = role === "SUPERVISOR";
  const toast = useToast();
  const d = derive(record);
  const order = record.orderName || "";
  const proc = PROC_OF[record.sheet] || "Submitter";
  const isUploader = proc === "Uploader";
  const isReturn = proc === "Modifier" || proc === "More Info";   // record was sent back to be fixed / clarified
  const statuses = isUploader ? STATUSES_UPLOADER : STATUSES_SUBMITTER;
  const sheetInbox = raw(record, "sValue") || "";

  const [cpts, setCpts] = useState(isReturn ? [] : splitCodes(record.submittedCpts));
  const [icds, setIcds] = useState(isReturn ? [] : splitCodes(record.submittedIcds));
  const initVal = (v) => (isReturn ? "" : (v || ""));   // Modification/More Info start fresh; old values are shown on the left
  const [method, setMethod] = useState(initVal(record.submissionMethod));
  // A submission in the tool is always by a PHS person; default their group to PHS and their name to the
  // logged-in user's CAS name (only for a fresh Submission, and only when not already set on the record).
  const isSubmitterProc = !isUploader && !isReturn;
  const myCas = (user && (user.fullName || [user.firstName, user.lastName].filter(Boolean).join(" ") || user.casName || user.name || user.username)) || "";
  const [submitterName, setSubmitterName] = useState(record.submitterName || record.workedByName || (isSubmitterProc ? myCas : ""));
  const [submitterGroup, setSubmitterGroup] = useState(record.submitterGroup || (isSubmitterProc ? "PHS" : ""));
  const [groupOptions, setGroupOptions] = useState([]);
  useEffect(() => { let a = true; try { if (api.casGroups) Promise.resolve(api.casGroups({ silent: true })).then((g) => { if (a) setGroupOptions(g || []); }).catch(() => {}); } catch { /* older api.js */ } return () => { a = false; }; }, []);
  const [casNames, setCasNames] = useState([]);
  useEffect(() => {
    let alive = true;
    if (submitterGroup && api.casNames) {
      try { Promise.resolve(api.casNames(submitterGroup, { silent: true })).then((n) => { if (alive) setCasNames(n || []); }).catch(() => { if (alive) setCasNames([]); }); }
      catch { if (alive) setCasNames([]); }
    } else setCasNames([]);
    return () => { alive = false; };
  }, [submitterGroup]);
  const [status, setStatus] = useState(initVal(record.paStatus));
  const [statusDate, setStatusDate] = useState(isReturn ? "" : (record.statusDate || ""));
  const [authNumber, setAuthNumber] = useState(initVal(record.authNumber));
  const [requestNumber, setRequestNumber] = useState(initVal(record.requestNumber));
  const [reason, setReason] = useState(initVal(record.reason));
  const [futureDate, setFutureDate] = useState(initVal(record.reviewFutureDate));   // "date mentioned in the inbox" when reason = Future dates
  const [otherMode, setOtherMode] = useState(false);   // "Other — write a reason" is selected
  const [inbox, setInbox] = useState(initVal(record.inboxValue));
  const [needHelp, setNeedHelp] = useState(initVal(record.needHelp));
  const [reasonForHelp, setReasonForHelp] = useState(initVal(record.reasonForHelp));
  const [remarks, setRemarks] = useState(initVal(record.remarks));
  const [faxNumber, setFaxNumber] = useState(initVal(record.faxNumber));
  const [phoneNumber, setPhoneNumber] = useState(initVal(record.phoneNumber));
  const [uploaderNote, setUploaderNote] = useState(initVal(record.uploaderNote));
  const [uploaderRemarks, setUploaderRemarks] = useState(initVal(record.uploaderRemarks));
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);   // keeps the loader up briefly for the save → ✓ confirmation
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);
  const [issueReq, setIssueReq] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [issueBusy, setIssueBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);   // guidelines-from-Assist modal
  // Mandatory gate: the worker must attach a guideline ("I used this") before any save/complete/status.
  // Admin/Supervisor are exempt (oversight). The backend enforces this too; this is the UX side.
  // When the guideline feature is turned off, everyone is "exempt": no gate, no block, no fetch/record.
  const glExempt = !GUIDELINES_ENABLED || ["ADMIN", "SUPER_ADMIN", "SUPERVISOR"].includes(role);
  const [glCodes, setGlCodes] = useState([]);   // guideline IDs this user has copied for THIS record
  const [glCode, setGlCode] = useState("");      // the ID typed/pasted in the field
  const [noGuideline, setNoGuideline] = useState(false);   // worker reported "no guideline exists"
  const [revCode, setRevCode] = useState("");      // direct Guideline-ID entry for a no-guideline Reviewed record
  const [revChk, setRevChk] = useState(null);      // null | "checking" | { ok:boolean, g? } — validation result
  useEffect(() => {
    let alive = true;
    if (glExempt) return;
    api.guidelineUsedByMe(record.id, { silent: true }).then((r) => {
      if (!alive || !r) return;
      if (Array.isArray(r.codes)) {
        setGlCodes(r.codes);
        if (r.codes.includes("NONE")) setNoGuideline(true);   // already reported earlier
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [record.id, glExempt]);
  const isSubmission = record.sheet === "SUBMISSION";
  // Reviewed records don't need a guideline (per spec) — exempt from the gate entirely.
  // Only a record ALREADY kept in Reviewed (re-opened for its daily remark) skips the guideline gate.
  // A submitter newly keeping a record in Reviewed must still attach a guideline (first-time entry).
  const reviewedExempt = record.paStatus === "Reviewed" || !!record.reviewedSince;
  // Later stages (End Process / Modification / More Info) inherit the guideline a prior stage applied.
  const [inherited, setInherited] = useState(null);     // { code, byName, stage } — the submitter/prior guideline
  const [inheritedG, setInheritedG] = useState(null);   // the full inherited guideline (for the kept-usage audit row)
  const [changing, setChanging] = useState(false);      // worker clicked "Change guideline" → enter a new code
  const [viewCode, setViewCode] = useState(null);       // open the dialog straight to this code (view only)
  const [searchOpen, setSearchOpen] = useState(false);  // open the dialog directly in "Fetch by criteria" (Change flow)
  const glKeptRecorded = useRef(false);                 // guard: record the kept-inherited usage once
  const copiedThisOpen = useRef(false);                 // did the worker copy any code from the dialog this open?
  useEffect(() => {
    let alive = true;
    if (glExempt || (isSubmission && !reviewedExempt)) { setInherited(null); return; }
    api.guidelineInherited(record.id, { silent: true }).then((r) => {
      if (!alive || !r || !r.code) return;
      setInherited(r);
      api.guidelineByCode(r.code, { silent: true }).then((g) => { if (alive && g) setInheritedG(g); }).catch(() => {});
    }).catch(() => {});
    return () => { alive = false; };
  }, [record.id, glExempt, isSubmission, reviewedExempt]);
  // Validate a directly-typed Guideline ID (no-guideline Reviewed record) against the guidelines system —
  // only a code that actually exists can be attached.
  useEffect(() => {
    const code = revCode.trim();
    if (!code) { setRevChk(null); return; }
    let alive = true;
    setRevChk("checking");
    const t = setTimeout(() => {
      api.guidelineByCode(code)
        .then((g) => { if (alive) setRevChk(g && (g.orderName || g.clientName || g.code || g.guidelineId) ? { ok: true, g } : { ok: false }); })
        .catch(() => { if (alive) setRevChk({ ok: false }); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [revCode]);
  const hasInherited = !isSubmission && !!(inherited && inherited.code);
  const typedValid = glCode.trim() !== "" && glCodes.includes(glCode.trim());
  // Reviewed-kept records are NO LONGER exempt from the guideline gate. If one is already attached, changing is
  // optional; if none is attached, the worker MUST add a valid code before Save & close (same as Submission).
  const reviewedHasCode = !!(inherited && inherited.code);
  const glValid = glExempt || noGuideline
    || (reviewedExempt
        ? (reviewedHasCode ? (glCode.trim() === "" || typedValid) : typedValid)
        : (isSubmission ? typedValid : (hasInherited ? (glCode.trim() === "" || typedValid) : typedValid)));
  // #3: Refer-to-Physician / Specialty-Services coordination notice — who else holds the same Unique ID.
  const isReferRec = (() => { const o = (record.orderName || "").toLowerCase(); return o.includes("refer to physician") || o.includes("specialty services"); })();
  const [coUsers, setCoUsers] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!isReferRec) return;
    api.coAllocated(record.id, { silent: true }).then((u) => { if (alive && Array.isArray(u)) setCoUsers(u); }).catch(() => {});
    return () => { alive = false; };
  }, [record.id, isReferRec]);
  // Inline "no guideline" report, right beside the Guideline ID field
  const [nogPrompt, setNogPrompt] = useState(false);
  const [nogReason, setNogReason] = useState("");
  const [nogOrder, setNogOrder] = useState(record.orderName || "");
  const [nogBusy, setNogBusy] = useState(false);
  const submitNog = () => {
    if (!nogReason.trim() || !nogOrder.trim()) { toast("Enter the order name and a reason.", "err"); return; }
    setNogBusy(true);
    api.reportNoGuideline(record.id, { reason: nogReason.trim(), orderName: nogOrder.trim() })
      .then(() => { setNoGuideline(true); setNogPrompt(false); toast("Reported — you can complete this record now.", "ok"); })
      .catch(() => toast("Couldn’t send the report, please try again.", "err"))
      .finally(() => setNogBusy(false));
  };
  const [forceMod, setForceMod] = useState(false);   // End Process → "Send to modification" was clicked
  const touch = () => setDirty(true);

  // Daily review check-in: for records kept in Reviewed, log that the user opened it today and require a
  // daily remark on why it's still in Reviewed (feeds the Reviewed-aging tracking).
  const keptInReviewed = record.paStatus === "Reviewed" || !!record.reviewedSince;
  // The daily review note is the ASSIGNEE's discipline (PPAT-6.2 / 6.3). A SUPERVISOR / ADMIN / SUPER_ADMIN
  // who opens a kept-in-Reviewed record is doing "privileged work" (PPAT-5.1) and must NOT be forced to write
  // the user's daily note, nor be blocked from leaving — only the owning USER adds today's note. The note card
  // is shown to them read-only (for reference), never as an entry prompt.
  const isPrivilegedViewer = ["SUPERVISOR", "ADMIN", "SUPER_ADMIN"].includes((role || "").toUpperCase());
  const [checkinComment, setCheckinComment] = useState("");
  const [checkinBusy, setCheckinBusy] = useState(false);
  const [checkinDone, setCheckinDone] = useState(!!record.reviewCheckedToday);   // already remarked today → not blocked
  const [mustSaveClose, setMustSaveClose] = useState(false);   // saved today's note THIS session → must now Save & close (can't X / switch)
  const [checkinErr, setCheckinErr] = useState("");
  const [reviewHist, setReviewHist] = useState(null);   // all past remarks (every user) — persists across re-assignment
  const [remarksOpen, setRemarksOpen] = useState(false);   // collapse the earlier-notes list so it doesn't push the form down
  // NOTE: we intentionally do NOT log a bare "opened" on mount anymore. Previously, merely opening a
  // kept-in-Reviewed record wrote an opened_at with no comment; if the user then closed the browser tab
  // or logged out (which the in-app leave-guard can't block) the Reviewed-aging view showed a misleading
  // "opened · no remark". Now the daily check-in is recorded ONLY when the user actually saves today's
  // remark (reviewCheckin), so a record is either remarked (handled) or simply shows no activity.
  useEffect(() => { if (keptInReviewed && record.id && api.reviewHistory) api.reviewHistory(record.id, { silent: true }).then((h) => setReviewHist(h || [])).catch(() => setReviewHist([])); }, [record.id, checkinDone]); // eslint-disable-line
  // Calendar days kept in Reviewed (kept today = 0, next day = 1). A remark is only required from day 1 onward.
  const reviewDays = (() => {
    const t = record.reviewedSince || record.assignedAt; if (!t) return 0;
    const a = new Date(t), b = new Date();
    const da = new Date(a.getFullYear(), a.getMonth(), a.getDate());
    const db = new Date(b.getFullYear(), b.getMonth(), b.getDate());
    return Math.max(0, Math.round((db - da) / 86400000));
  })();
  // A record kept in Reviewed since a PRIOR day can't be left until the user saves today's remark (or completes
  // it). Kept today (0 days) is not asked. This blocks Save & close, the X, AND the browser Back button.
  const mustRemark = keptInReviewed && reviewDays >= 1 && !checkinDone && !isPrivilegedViewer;
  // Can't leave the open record while it still needs today's remark, OR after the note is saved but before
  // Save & close. Blocks the X, the browser Back button, and switching tabs (via workGuard below).
  const lockLeave = mustRemark || mustSaveClose;
  useEffect(() => {
    if (!lockLeave) return;
    window.history.pushState(null, "", window.location.href);
    const msg = mustSaveClose ? "Click Save & close to finish this record." : "Add today's review remark (or complete the record) before leaving.";
    const block = () => { window.history.pushState(null, "", window.location.href); toast(msg, "err"); };
    window.addEventListener("popstate", block);
    // Closing the browser tab / refreshing can't be blocked outright, but the native "Leave site?" prompt
    // warns the user that today's note is still unsaved. If they leave anyway, the auto-reopen below brings
    // them back to this record (no note) the next time they open the app.
    const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; return ""; };
    window.addEventListener("beforeunload", beforeUnload);
    return () => { window.removeEventListener("popstate", block); window.removeEventListener("beforeunload", beforeUnload); };
  }, [lockLeave, mustSaveClose]); // eslint-disable-line
  const saveCheckin = async () => {
    if (!checkinComment.trim()) { setCheckinErr("Please add a remark on why this record is still in Reviewed."); return; }
    setCheckinBusy(true); setCheckinErr("");
    try { await api.reviewCheckin(record.id, checkinComment.trim()); setCheckinDone(true); setMustSaveClose(true); toast("Note saved — now click Save & close to finish.", "ok"); }
    catch (e) { setCheckinErr(e.message || "Could not save the remark."); }
    finally { setCheckinBusy(false); }
  };

  // when the submitter picks a submission method, the status can only be Pending
  const statusOptions = statuses;

  const opts = inboxOptions(status, isUploader);
  const inboxIsChoice = opts.length > 1;
  const fixedInbox = opts.length === 1 ? opts[0] : "";
  const isReviewed = status === "Reviewed";
  // End Process record with no submitter details (worked by an outsider) → allow entering them on the left.
  const submitterEmpty = isUploader && !record.submittedCpts && !record.submittedIcds && !record.submissionMethod;
  // Resolved / Results Pending need BOTH the auth number and a request number, whatever the method.
  const needsReqAndAuth = STATUS_NEEDS_REQ_AND_AUTH.includes(status);
  const needsAuth = status === "Approved" || needsReqAndAuth;
  // Submission (portal) Reviewed reasons — "Future dates" is offered here and asks for the inbox date.
  const PORTAL_REVIEWED_REASONS = ["EVBV Needed", "No latest Path / Labs / Radiology Reports", "Radiation Oncology Notes Required", "Cpt Codes Clarification", "Unable to change the Inbox value", "Unable to update the Inbox", "MD notes unsigned", "Awaiting task", "Future dates"];
  // End Process Reviewed reasons — "Future dates" is NOT offered here.
  const ENDPROCESS_REVIEWED_REASONS = [...(REASON_OPTIONS.Reviewed || []).filter((x) => x !== "Future dates"), "Approved-Awaiting fax", "No response from IPA", "Modification needed (need to resubmit)"];
  // Reviewed: show all the review reasons regardless of how it was submitted (portal / call / fax).
  const reasonOpts = (status === "Reviewed" && record.sheet === "END_PROCESS") ? ENDPROCESS_REVIEWED_REASONS
    : (status === "Reviewed") ? PORTAL_REVIEWED_REASONS
    : (REASON_OPTIONS[status] || []);
  const rawReason = isUploader ? uploaderNote : reason;
  const setRawReason = isUploader ? setUploaderNote : setReason;
  const reasonInList = reasonOpts.includes(rawReason);
  // "Other" custom reason: on when the user picks it, or when the saved reason isn't one of the options.
  // rawReason always holds the real value (a picked option, or the typed custom text) — no sentinel is stored.
  const showOther = otherMode || (reasonOpts.length > 0 && !!rawReason && !reasonInList);
  const reasonValue = rawReason;
  const reasonRequired = reasonOpts.length > 0;   // if a reason list exists for this status, it must be chosen
  const needsReason = reasonRequired;
  // "Future dates" (Reviewed) needs the specific date mentioned in the inbox — mandatory.
  const isFutureDates = isReviewed && reasonValue === "Future dates";
  const needsReqNoSubmitter = !isUploader && status === "Denied";
  // "PA Not Required" and "Reviewed" are light-touch: the portal request / call reference number is optional.
  // Resolved / Results Pending also never require a request number — the authorization number is enough.
  const reqNoOptional = status === "PA Not Required" || status === "Reviewed" || needsReqAndAuth;
  // Approved via portal has no request number — don't ask for it.
  const portalNoReq = method === "Portal" && status === "Approved";
  // Resolved / Results Pending: block (hide/disable) the request-number field for Portal and Fax — only the
  // authorization number is needed there. Call keeps its reference field, but it is optional.
  const reqBlockedResolved = !isUploader && needsReqAndAuth && (method === "Portal" || method === "Fax" || !method);

  const validate = () => {
    setError("");
    if (submitterEmpty) {   // End Process with no submitter details → the entered details are required
      if (!cpts.length) { setError("Enter at least one CPT code."); return false; }
      if (!method) { setError("Choose how it was submitted (portal, call or fax)."); return false; }
      if (!submitterGroup) { setError("Choose the submitter group."); return false; }
      if (!submitterName.trim()) { setError("Enter the submitter name."); return false; }
    }
    if (!isUploader && method) {
      if (method === "Portal" && !reqNoOptional && !portalNoReq && !requestNumber.trim()) { setError("Request number is required for a portal submission."); return false; }
      // Call reference number is always optional — only the phone number is required for a call.
      if (method === "Call" && !phoneNumber.trim()) { setError("Phone number is required for a call submission."); return false; }
      if (method === "Fax" && !faxNumber.trim()) { setError("Fax number is required for a fax submission."); return false; }
    }
    if (status) {
      if (needsAuth && !authNumber.trim()) { setError(needsReqAndAuth ? ("Authorization number is required for " + status + ".") : "Authorization number is required to approve."); return false; }
      // Resolved / Results Pending no longer require a request number — the authorization number is enough.
      if (needsReqNoSubmitter && !requestNumber.trim()) { setError("Request number is required for a denial."); return false; }
      if (reasonRequired && !reasonValue.trim()) { setError(showOther ? "Please write a reason." : "Please select a reason."); return false; }
      if (isFutureDates && !futureDate.trim()) { setError("Enter the date mentioned in the inbox (required for Future dates)."); return false; }
      if (!isUploader && status === "Pending" && !method) { setError("Choose how the request was submitted (portal, call or fax)."); return false; }
      if (inboxIsChoice && !inbox) { setError("Choose an inbox value."); return false; }
      if (STATUS_DATE_REQUIRED.includes(status) && !statusDate) { setError("Please choose the date this status was set."); return false; }
    }
    if (needHelp && !reasonForHelp.trim()) { setError("Please give a reason for the help request."); return false; }
    return true;
  };

  const inboxToSend = forceMod ? "Modification Needed"
    : (!isUploader && isReviewed) ? (sheetInbox || null) : (inboxIsChoice ? inbox : (fixedInbox || null));
  const isSendBack = inboxToSend === "Modification Needed" || inboxToSend === "More Info Needed";

  const doSave = async (complete, issueType, issueReason) => {
    if (!validate()) return false;
    if (!glValid) {
      setError('Open “View Guidelines”, copy the 5-digit Guideline ID of the guideline you used, and paste it in the Guideline ID box before saving or completing.');
      toast("Enter the Guideline ID from the dialog first.", "err");
      return false;
    }
    setBusy(true);
    try {
      // Later stage, guideline KEPT (not changed) → record a usage row for THIS stage BEFORE completing,
      // while the record is still assigned to this worker (completion routing clears the assignment). When a
      // code is copied/changed, the guidelines dialog already recorded the row, so skip then.
      if (complete && !glExempt && !reviewedExempt && hasInherited && !typedValid && !copiedThisOpen.current && !glKeptRecorded.current) {
        const g = inheritedG || {};
        try {
          await api.recordGuidelineUsage(record.id, {
            guidelineId: inherited.code, stage: record.sheet,
            clientName: g.clientName, specialtyName: g.specialtyName, orderTypeName: g.orderTypeName,
            orderName: g.orderName || record.orderName, anatomicalLocationName: g.anatomicalLocationName, techniqueName: g.techniqueName,
            status: g.status, cptCode: g.cptCode, cptDescription: g.cptDescription, icdCode: g.icdCode, icdDescription: g.icdDescription,
          });
          glKeptRecorded.current = true;
        } catch (e) { /* audit is best-effort; never block completion */ }
      }
      await api.setRecordWork(record.id, {
        submittedCpts: cpts.join(", "), submittedIcds: icds.join(", "), requests: [order],
        paStatus: status || null, statusDate: (STATUS_NEEDS_DATE.includes(status) ? (statusDate || null) : null), authNumber, requestNumber, reason: (reasonOpts.length ? reason : ""),
        reviewFutureDate: isFutureDates ? (futureDate || null) : null,
        inboxValue: inboxToSend,
        faxNumber, phoneNumber, submissionMethod: method || null, submitterName: submitterName || null, submitterGroup: submitterGroup || null,
        needHelp: needHelp || null, reasonForHelp, remarks,
        uploaderNote: isUploader ? uploaderNote : null,
        uploaderRemarks: isUploader ? uploaderRemarks : null,
        issueType: issueType || null, issueReason: issueReason || null,
        complete,
      });
      return true;
    } catch (ex) { setError(ex.message); return false; }
    finally { setBusy(false); }
  };

  // Keep the loader visible a beat so the save always reads as spinner → confirmation → back to the list.
  const settle = (ms = 550) => new Promise((r) => window.setTimeout(r, ms));
  const saveAndClose = async () => {
    if (mustRemark) { setCheckinErr("Add today's review remark below (or complete the record) before leaving."); toast("Add today's review remark before leaving.", "err"); return; }
    setFinishing(true);
    if (await doSave(false)) { await settle(); toast("✓ Saved — still in your list", "ok"); onSaved(); }
    else setFinishing(false);
  };
  const complete = () => { if (!status) { setError("Please select a status before completing."); return; } if (!validate()) return; setShowGuidelines(false); setConfirmComplete(true); };
  // End Process: send straight to Modification. Reuses the "Modification Needed" issue flow, so it asks
  // whose issue + reason, then routes to Modification staying with this same user.
  const sendToMod = () => { if (!validate()) return; setForceMod(true); setIssueReq(true); };
  const proceedComplete = async () => {
    setConfirmComplete(false);
    if (isSendBack) { setIssueReq(true); return; }   // ask whose issue before sending back
    if (await doSave(true)) { await settle(); toast("✓ Completed", "ok"); onSaved(); }
  };
  const completeWithIssue = async (issueType, issueReason) => {
    setIssueBusy(true);
    const ok = await doSave(true, issueType, issueReason);
    if (ok) { await settle(); }
    setIssueBusy(false);
    if (ok) { setIssueReq(false); toast("✓ Sent back", "ok"); onSaved(); }
  };
  const tryClose = () => {
    if (mustRemark) { setCheckinErr("Add today's review remark below (or complete the record) before leaving."); toast("Add today's review remark before leaving.", "err"); return; }
    if (mustSaveClose) { setCheckinErr("You've saved today's note — now click Save & close to finish."); toast("Please click Save & close to finish.", "err"); return; }
    if (dirty) setConfirmClose(true); else onClose();
  };

  // expose unsaved-work state so switching client/process can offer to save first.
  // While a Reviewed remark is still required, block the auto-save-on-switch too (return no save handler).
  useEffect(() => { workGuard.current = lockLeave ? { dirty: true, save: async () => false, block: true, message: mustSaveClose ? "Please Save & close this reviewed record first." : "Add today's review remark first." } : { dirty, save: () => doSave(false) }; });
  useEffect(() => () => { workGuard.current = null; }, []);

  const detailsCard = (
    <div className="wv2-block">
      <h3 className="wv-h">Record details</h3>
      <div className="wv-details">
        <div className="kv"><span>PID (MRN)</span><b className="mono">{record.mrn}</b></div>
        <div className="kv"><span>Unique ID</span><b className="mono">{record.uniqueId}</b></div>
        <div className="kv"><span>Group</span><b>{record.productionGroup || d.location || "—"}</b></div>
        <div className="kv"><span>Clinic Location</span><b>{d.clinic ? (d.locationCode ? `${d.clinic} (${d.locationCode})` : d.clinic) : "—"}</b></div>
        <div className="kv"><span>Order</span><b>{order || "—"}</b></div>
        <div className="kv"><span>OrderMD</span><b>{d.orderMD || "—"}</b></div>
        <div className="kv"><span>Order Date</span><b>{d.orderDate || "—"}</b></div>
        <div className="kv"><span>RTC Date</span><b>{d.rtcDate || "—"}</b></div>
        <div className="kv"><span>Urgent / Routine</span><b>{d.urgentRoutine || "—"}</b></div>
        <div className="kv"><span>Clinic Priority</span><b>{d.clinicPriority || "—"}</b></div>
        <div className="kv"><span>Payor / IPA</span><b>{d.insurance || "—"}</b></div>
        <div className="kv"><span>Portal</span><b>{d.portal || "—"}</b></div>
      </div>
      {record.assignedAt && (() => {
        const dt = new Date(record.assignedAt);
        const isToday = dt.toDateString() === new Date().toDateString();
        const dstr = dt.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
        return (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: isToday ? "#ecfdf5" : "#f1f5f9", color: isToday ? "#065f46" : "#475569", border: `1px solid ${isToday ? "#a7f3d0" : "#e2e8f0"}`, borderRadius: 999, padding: "4px 12px", fontSize: 11.5, fontWeight: 700 }}>
              {isToday ? "Assigned today" : "Assigned on"} · {dstr}
            </span>
          </div>
        );
      })()}

      {isUploader && (
        <div className={"wv-submitter" + (submitterEmpty ? " wv-submitter-empty" : "")}>
          <h4 className="wv-h4">What the submitter entered</h4>
          {submitterEmpty ? (
            <div className="wv-enter-empty">
              <div className="wv-enter-empty-note">Enter the submitter details — all fields required except ICDs and remarks (optional).</div>
              <div className="wv-enter-grid">
                <CodeChips label="CPTs" codes={cpts} setCodes={setCpts} validate={(v) => CPT_RE.test(v)} placeholder="e.g. 78815" hint="Type a code and press Enter" onDirty={touch} />
                <CodeChips label="ICDs (optional)" codes={icds} setCodes={setIcds} validate={(v) => ICD_RE.test(v)} placeholder="e.g. C50.911" hint="Optional — type a code and press Enter" onDirty={touch} />
                <div className="field req"><label>Submitted via</label>
                  <select className="inp" value={method} onChange={(e) => { setMethod(e.target.value); touch(); }}>
                    <option value="">Select…</option>
                    {SUBMIT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select></div>
                <div className="field req"><label>Submitter group</label>
                  <select className="inp" value={submitterGroup} onChange={(e) => { setSubmitterGroup(e.target.value); setSubmitterName(""); touch(); }}>
                    <option value="">Select group…</option>
                    {groupOptions.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select></div>
                <div className="field req"><label>CAS name</label>
                  {casNames.length
                    ? <select className="inp" value={submitterName} onChange={(e) => { setSubmitterName(e.target.value); touch(); }} disabled={!submitterGroup}>
                        <option value="">Select…</option>
                        {casNames.map((n) => {
                          const cas = typeof n === "string" ? n : (n.cas || "");
                          const full = typeof n === "string" ? "" : (n.full || "");
                          return <option key={cas} value={cas}>{full && full !== cas ? `${cas} (${full})` : cas}</option>;
                        })}
                      </select>
                    : <input className="inp" value={submitterName} onChange={(e) => { setSubmitterName(e.target.value); touch(); }} placeholder={submitterGroup ? "No CAS names for this group" : "Select a group first"} disabled={!submitterGroup} />}
                </div>
                <div className="field"><label>Remarks (optional)</label>
                  <input className="inp" value={remarks} onChange={(e) => { setRemarks(e.target.value); touch(); }} placeholder="Remarks (optional)" /></div>
              </div>
            </div>
          ) : (
            <>
              {(record.workedByName || record.workedAt || record.submitterName) && (
                <div className="wv-workedby"><Icon.history width="14" height="14" /> Submitted by <b>{record.submitterName || record.workedByName || "a user"}</b>{record.workedAt ? <> on <b>{dayOnly(record.workedAt)}</b></> : null}</div>
              )}
              <div className="wv-details">
                <div className="kv"><span>Submitted via</span><b>{record.submissionMethod || "—"}</b></div>
                {record.submissionMethod === "Portal" && <div className="kv"><span>Request number</span><b>{record.requestNumber || "—"}</b></div>}
                {record.submissionMethod === "Call" && <div className="kv"><span>Call reference number</span><b>{record.requestNumber || "—"}</b></div>}
                {record.submissionMethod === "Call" && <div className="kv"><span>Phone number</span><b>{record.phoneNumber || "—"}</b></div>}
                {record.submissionMethod === "Fax" && <div className="kv"><span>Fax number</span><b>{record.faxNumber || "—"}</b></div>}
                <div className="kv"><span>Submitted CPTs</span><b>{record.submittedCpts || "—"}</b></div>
                <div className="kv"><span>Submitted ICDs</span><b>{record.submittedIcds || "—"}</b></div>
                <div className="kv"><span>Submitter group</span><b>{record.submitterGroup || "—"}</b></div>
                <div className="kv"><span>Submitter name</span><b>{record.submitterName || "—"}</b></div>
                <div className="kv"><span>Submitter reason</span><b>{record.reason || "—"}</b></div>
                <div className="kv"><span>Remarks</span><b>{record.remarks || record.uploaderNote || "—"}</b></div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );

  const returnPanel = isReturn ? (
    <section className="wv-card wv-return-card">
      <h3 className="wv-h wv-return-h"><Icon.history width="16" height="16" /> Sent back for {proc === "Modifier" ? "Modification" : "More Info"}</h3>
      <p className="wv-return-note">Everything entered so far is shown below, newest first. The fields on the right start blank — enter fresh values.</p>
      <WorkHistory raw={record.workHistory} record={record} />
    </section>
  ) : null;

  const inboxField = (!isUploader && isReviewed) ? (
      <div className="field"><label>Inbox value (from sheet)</label>
        <input className="inp" value={sheetInbox || "—"} readOnly disabled /></div>
    ) : inboxIsChoice ? (
      <div className="field req"><label>Inbox value</label>
        <select className="inp" value={inbox} onChange={(e) => { setInbox(e.target.value); touch(); }}>
          <option value="">Select…</option>
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select></div>
    ) : (
      <div className={"field" + (fixedInbox ? "" : " field-locked")}><label>Inbox value</label>
        <input className="inp" value={fixedInbox || ""} readOnly disabled placeholder="Set once a status is chosen" /></div>
    );


  const needHelpBlock = !isSupervisor ? (
    <div className="field"><label>Need help?</label>
      <div className="chip-select">
        {["", "Frontend", "Supervisor"].map((h) => (
          <button type="button" key={h || "none"} className={"chip-opt" + (needHelp === h ? " on" : "")} onClick={() => { setNeedHelp(h); touch(); }}>
            {needHelp === h && <Icon.check width="13" height="13" />}{h || "No"}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  // Manual code-entry controls (paste a copied ID, or report "no guidelines"). Shared by the first-time
  // Submission gate and the "Change guideline" flow on later stages.
  const entryControls = (emptyHint) => noGuideline ? (
    <div className="hint" style={{ color: "#16a34a", fontWeight: 700 }}>✓ Reported “no guideline” — you can complete this record.</div>
  ) : (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input className="inp" value={glCode} maxLength={12}
          onChange={(e) => setGlCode(e.target.value)}
          placeholder="Paste the ID from the dialog"
          title="Copy a 5-digit Guideline ID in the View Guidelines dialog and paste it here"
          style={{ flex: 1, minWidth: 120, fontFamily: "ui-monospace,Menlo,Consolas,monospace", letterSpacing: ".06em", borderColor: glCode.trim() ? (glValid ? "#16a34a" : "#dc2626") : undefined }} />
        <button type="button" className="btn" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap", color: "#dc2626", border: "1px solid #f3b4b4", background: "#fff5f5", fontWeight: 700 }}
          onClick={() => setNogPrompt(true)} title="If no guideline exists for this order, report it and you can complete">
          No guidelines found
        </button>
      </div>
      {glCode.trim()
        ? <div className="hint" style={{ color: glValid ? "#16a34a" : "#dc2626", fontWeight: 700 }}>{glValid ? "✓ Matches a guideline you copied" : "✗ Not an ID you copied from the dialog"}</div>
        : emptyHint}
    </>
  );

  const entryBlock = (
    <div className="field req wv-gl">
      <label>Guideline ID</label>
      {entryControls(<div className="hint">Required — copy a Guideline ID, or click “No guidelines found”.</div>)}
    </div>
  );

  // Later stages: the inherited guideline is applied by default. "Change guideline" (red) reveals the manual
  // entry so the worker can attach a different code; otherwise the submitter's code is kept on completion.
  const inheritedGlBlock = (
    <div className="field req wv-gl">
      <label>Guideline</label>
      {!changing ? (
        <>
          <div className="hint" style={{ color: "#166534", fontWeight: 700 }}>
            {`✓ Using guideline ${inherited ? inherited.code : ""} from ${SHEET_NAME[inherited && inherited.stage] || "an earlier stage"}${inherited && inherited.byName ? " · " + inherited.byName : ""}.`}
          </div>
          <div style={{ marginTop: 6 }}>
            <button type="button" className="btn" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap", color: "#dc2626", border: "1px solid #f3b4b4", background: "#fff5f5", fontWeight: 700 }}
              onClick={() => { setChanging(true); setGlCode(""); setViewCode(null); setSearchOpen(true); setShowGuidelines(true); }}
              title="Use a different guideline if this one is wrong">
              Change guideline
            </button>
          </div>
        </>
      ) : (
        <>
          {entryControls(
            <div className="hint" style={{ color: "#64748b" }}>Leave empty to keep the submitter’s guideline (<b>{inherited ? inherited.code : ""}</b>), or paste a new code.</div>
          )}
          <div style={{ marginTop: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap" }}
              onClick={() => { setViewCode(null); setSearchOpen(true); setShowGuidelines(true); }}
              title="Open the search to pick a different guideline">
              Find a guideline
            </button>
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap" }}
              onClick={() => { setChanging(false); setGlCode(""); setNogPrompt(false); setShowGuidelines(false); }}
              title="Cancel — keep the guideline the submitter used">
              Cancel change
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Submission = first-time entry (manual ID). Reviewed/elevated = no gate. Later stages = inherited (+ change).
  // Reviewed records never NEED a guideline (old ones predate the feature), but should still let you attach one
  // (Add) or change an attached one (Change) — optional, never blocks Save & close.
  const reviewedGlBlock = (
    <div className="field wv-gl">
      <label>Guideline <span style={{ color: "#64748b", fontWeight: 500 }}>(optional)</span></label>
      {inherited && inherited.code ? (
        !changing ? (
        <>
          <div className="hint" style={{ color: "#166534", fontWeight: 700 }}>
            {`✓ Attached: ${inherited.code}${inherited.byName ? " · " + inherited.byName : ""}`}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap", fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontWeight: 700 }}
              onClick={() => { setViewCode(inherited.code); setSearchOpen(false); setShowGuidelines(true); }}>🔎 View</button>
            <button type="button" className="btn" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap", color: "#dc2626", border: "1px solid #f3b4b4", background: "#fff5f5", fontWeight: 700 }}
              onClick={() => { setChanging(true); setRevCode(""); setRevChk(null); }}>Change guideline</button>
          </div>
        </>
        ) : (
        <>
          <div className="hint" style={{ color: "#64748b" }}>Enter a new Guideline ID to replace <b>{inherited.code}</b>, or cancel to keep it.</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
            <input className="inp" value={revCode} maxLength={12}
              onChange={(e) => setRevCode(e.target.value)}
              placeholder="Enter Guideline ID"
              style={{ minWidth: 150, fontFamily: "ui-monospace,Menlo,Consolas,monospace", letterSpacing: ".06em",
                borderColor: (revChk && revChk !== "checking") ? (revChk.ok ? "#16a34a" : "#dc2626") : undefined }} />
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap", fontWeight: 700 }}
              disabled={!(revChk && revChk !== "checking" && revChk.ok)}
              onClick={() => { const code = revCode.trim(); setInherited({ code }); setGlCodes((cs) => (cs.includes(code) ? cs : [...cs, code])); setChanging(false); setRevCode(""); setRevChk(null); }}>
              Change to this
            </button>
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap" }}
              onClick={() => { setChanging(false); setRevCode(""); setRevChk(null); }}>
              Cancel change
            </button>
          </div>
          {revChk === "checking" && <div className="hint" style={{ color: "#64748b" }}>Checking…</div>}
          {revChk && revChk !== "checking" && (
            <div className="hint" style={{ color: revChk.ok ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
              {revChk.ok ? `✓ ${(revChk.g && (revChk.g.orderName || revChk.g.clientName)) || "Valid guideline"}` : "✗ No guideline with that ID"}
            </div>
          )}
        </>
        )
      ) : (
        <>
          <div className="hint" style={{ color: "#64748b" }}>No guideline attached — enter a Guideline ID to attach one (optional).</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
            <input className="inp" value={revCode} maxLength={12}
              onChange={(e) => setRevCode(e.target.value)}
              placeholder="Enter Guideline ID"
              style={{ minWidth: 150, fontFamily: "ui-monospace,Menlo,Consolas,monospace", letterSpacing: ".06em",
                borderColor: (revChk && revChk !== "checking") ? (revChk.ok ? "#16a34a" : "#dc2626") : undefined }} />
            <button type="button" className="btn btn-ghost" style={{ fontSize: 12, padding: "6px 10px", whiteSpace: "nowrap", fontWeight: 700 }}
              disabled={!(revChk && revChk !== "checking" && revChk.ok)}
              onClick={() => {
                const code = revCode.trim();
                setInherited({ code });
                setGlCodes((cs) => (cs.includes(code) ? cs : [...cs, code]));
                setRevCode(""); setRevChk(null);
              }}>Attach</button>
          </div>
          {revChk === "checking" && <div className="hint" style={{ color: "#64748b" }}>Checking…</div>}
          {revChk && revChk !== "checking" && (
            <div className="hint" style={{ color: revChk.ok ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
              {revChk.ok
                ? `✓ ${(revChk.g && (revChk.g.orderName || revChk.g.clientName)) || "Valid guideline"}`
                : "✗ No guideline with that ID"}
            </div>
          )}
        </>
      )}
    </div>
  );

  const guidelineIdBlock = glExempt ? null
    : reviewedExempt ? (reviewedHasCode ? inheritedGlBlock : entryBlock)   // same system as End Process / Submission
    : isSubmission ? entryBlock
    : hasInherited ? inheritedGlBlock
    : entryBlock;

  const remarksBlock = (
    <div className="field"><label>Remarks (optional)</label>
      <textarea className="inp" rows={2} value={isUploader ? uploaderRemarks : remarks}
        onChange={(e) => { (isUploader ? setUploaderRemarks : setRemarks)(e.target.value); touch(); }}
        placeholder="Remarks (free text)" /></div>
  );

  const reasonBlock = (
    <div className={"field" + (reasonOpts.length > 0 ? " req" : " field-locked")}>
      <label>Reason</label>
      {reasonOpts.length > 0 ? (
        <>
          <select className="inp" value={showOther ? OTHER_REASON : (reasonInList ? rawReason : "")}
            onChange={(e) => {
              const v = e.target.value;
              if (v === OTHER_REASON) { setOtherMode(true); setRawReason(""); }
              else { setOtherMode(false); setRawReason(v); }
              touch();
            }}>
            <option value="">Select a reason…</option>
            {reasonOpts.map((r) => <option key={r} value={r}>{r}</option>)}
            <option value={OTHER_REASON}>{OTHER_REASON}</option>
          </select>
          {showOther && (
            <input className="inp" style={{ marginTop: 8 }} value={rawReason}
              onChange={(e) => { setRawReason(e.target.value); touch(); }}
              placeholder="Write the reason (required)" />
          )}
          {isFutureDates && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
              <label style={{ display: "block", fontWeight: 800, color: "#1e3a8a", fontSize: 13, marginBottom: 6 }}>Future date <span style={{ color: "#dc2626" }}>*</span></label>
              <input className="inp" type="date" style={{ width: 190, height: 38 }} value={_toISO(futureDate)}
                onChange={(e) => { setFutureDate(_fromISO(e.target.value)); touch(); }} title="Pick the date from the calendar" />
              <div style={{ marginTop: 6, fontSize: 12.5, color: "#1e40af" }}>What is the date mentioned in the inbox</div>
            </div>
          )}
        </>
      ) : (
        <select className="inp" disabled value=""><option>No reason needed for this status</option></select>
      )}
    </div>
  );

  // Flat fields — each one is a direct cell in the work grid so the layout is predictable and never shifts.
  const helpRemarks = (
    <>
      {inboxField}
      {reasonBlock}
      {needHelpBlock}
      {!isSupervisor && <div className={"field" + (needHelp ? " req" : " field-locked")}><label>Reason for help</label>
        <input className="inp" value={reasonForHelp} disabled={!needHelp} onChange={(e) => { setReasonForHelp(e.target.value); touch(); }} placeholder={needHelp ? "What do you need help with?" : "Turn on Need help to add a note"} /></div>}
      {guidelineIdBlock}
      {remarksBlock}
    </>
  );

  const statusField = (
    <div className="field"><label>Status</label>
      <select className="inp" value={status} onChange={(e) => {
        const v = e.target.value; setStatus(v); setInbox("");
        // the reason list depends on the status, so clear the previous reason + custom-reason mode
        setOtherMode(false); setReason(""); if (!submitterEmpty) setUploaderNote("");
        // changing the status clears the free-text remarks so a fresh remark can be written for the new status
        setRemarks(""); setUploaderRemarks("");
        if (STATUS_NEEDS_DATE.includes(v)) { if (!statusDate) setStatusDate(_todayMDY()); }
        else setStatusDate("");
        touch();
      }}>
        <option value="">Select status…</option>
        {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
    </div>
  );

  const dateActive = STATUS_NEEDS_DATE.includes(status);
  const statusDateField = (
    <div className={"field" + (dateActive && STATUS_DATE_REQUIRED.includes(status) ? " req" : "") + (dateActive ? "" : " field-locked")}>
      <label>Status date {dateActive && status === "Withdraw" ? <span className="hint-inline">(optional)</span> : null}</label>
      <input className="inp" type="date" value={_toISO(statusDate)} disabled={!dateActive} onChange={(e) => { setStatusDate(_fromISO(e.target.value)); touch(); }} />
    </div>
  );
  const submittedViaField = (
    <div className="field"><label>Submitted via</label>
      <select className="inp" value={method} onChange={(e) => { setMethod(e.target.value); touch(); }}>
        <option value="">Select…</option>
        {SUBMIT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
  // Request number is one fixed field, unlocked for Portal/Call or when a status needs it.
  const reqActive = !reqBlockedResolved && ((method === "Portal" && !portalNoReq) || method === "Call" || needsReqAndAuth || (needsReqNoSubmitter && !(method === "Portal" || method === "Call")));
  const reqLabel = method === "Call" ? "Call reference number" : "Request number";
  // Call reference number is always optional, so never show it as required for a Call.
  const refOptionalNow = reqNoOptional || method === "Call";
  const methodFields = (
    <>
      <div className={"field" + (reqActive && !refOptionalNow ? " req" : "") + (reqActive ? "" : " field-locked")}>
        <label>{reqLabel} {reqActive && refOptionalNow ? <span className="hint-inline">(optional)</span> : null}</label>
        <input className="inp" value={requestNumber} disabled={!reqActive} onChange={(e) => { setRequestNumber(e.target.value); touch(); }} placeholder={reqLabel} /></div>
      <div className={"field" + (method === "Call" ? " req" : " field-locked")}><label>Phone number</label>
        <input className="inp" value={phoneNumber} disabled={method !== "Call"} onChange={(e) => { setPhoneNumber(e.target.value); touch(); }} placeholder="e.g. (555) 123-4567" /></div>
      <div className={"field" + (method === "Fax" ? " req" : " field-locked")}><label>Fax number</label>
        <input className="inp" value={faxNumber} disabled={method !== "Fax"} onChange={(e) => { setFaxNumber(e.target.value); touch(); }} placeholder="e.g. (555) 123-4567" /></div>
    </>
  );
  const statusExtraFields = (
    <>
      <div className={"field" + (needsAuth ? " req" : " field-locked")}><label>Authorization number</label>
        <input className="inp" value={authNumber} disabled={!needsAuth} onChange={(e) => { setAuthNumber(e.target.value); touch(); }} placeholder="Enter auth number" /></div>
      {/* End Process has no submission-method fields; surface an OPTIONAL request number for Resolved / Results Pending — only the auth number is required */}
      {needsReqAndAuth && isUploader && (
        <div className="field"><label>Request number <span className="hint-inline">(optional)</span></label>
          <input className="inp" value={requestNumber} onChange={(e) => { setRequestNumber(e.target.value); touch(); }} placeholder="Enter request number" /></div>
      )}
    </>
  );

  return (
    <div className="workview">
      {(busy || finishing) && <div className="switch-overlay" style={{ position: "fixed" }}><SwitchSpinner /></div>}
      <style>{`.workview .wv-details .kv span{color:#6366f1!important;font-weight:700!important;text-transform:uppercase;letter-spacing:.04em;font-size:10.5px!important}.workview .wv-details .kv b{color:#0f172a!important;font-weight:700!important}`}</style>
      <div className="wv-bar">
        <div className="wv-title"><span className="wv-pid">PID {record.mrn}</span><span className="wv-order">{order}</span>{isUploader && <span className="wv-role-tag">End Process</span>}</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {/* View Guidelines: only where the worker needs to browse — Submission, a change, or no inherited one.
              A reviewed-kept record that already carries the submitter's guideline shows the code chip instead
              (below); "Change guideline" in the body is how they browse/fetch a different one. */}
          {GUIDELINES_ENABLED && (isSubmission || !hasInherited) && !changing && !(reviewedExempt && inherited && inherited.code) && (
            <button className={"btn " + (glValid ? "btn-ghost" : "btn-primary")} style={{ whiteSpace: "nowrap" }}
              onClick={() => { setViewCode(null); setSearchOpen(changing); setShowGuidelines(true); }}
              title={glValid ? "View authorization guidelines for this order" : "Required: copy a Guideline ID and paste it in the Guideline ID box before saving"}>
              <Icon.history width="15" height="15" /> {glValid ? "View Guidelines" : "View Guidelines — required"}
            </button>
          )}
          {/* Later stages, and any reviewed-kept record that already has one: show the submitter's guideline
              code — click to view it (no browse/back/fetch). */}
          {GUIDELINES_ENABLED && (!isSubmission || reviewedExempt) && inherited && inherited.code && (
            <button type="button" className="btn btn-ghost wv-gl-chip" style={{ whiteSpace: "nowrap", fontFamily: "ui-monospace,Menlo,Consolas,monospace", letterSpacing: ".04em", fontWeight: 700 }}
              onClick={() => { setViewCode(inherited.code); setSearchOpen(false); setShowGuidelines(true); }}
              title={`Submitter used guideline ${inherited.code} — click to view`}>
              Submitter: {inherited.code}
            </button>
          )}
          {/* Close X — always shown. Red on a reviewed-kept record, and tryClose enforces the restrictions
              (must add today's note, then Save & close; no direct close / tab switch). */}
          <button className="wv-x" onClick={tryClose} title="Close" aria-label="Close"
            style={{ marginLeft: 0, ...(keptInReviewed && reviewDays >= 1 ? { color: "#dc2626", borderColor: "#fca5a5", background: "#fff5f5" } : {}) }}>
            <Icon.x width="20" height="20" /></button>
        </div>
      </div>

      {coUsers.length > 0 && (
        <div style={{ margin: "0 20px 10px", padding: "11px 14px", background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 10, color: "#92400e", fontWeight: 600, fontSize: 13, lineHeight: 1.55 }}>
          ⚠ This Specialty / Refer-to-Physician record (same Unique ID) is also allocated to: <b>{coUsers.join(", ")}</b>. Please coordinate with the admin and these users and work carefully to avoid duplicate work.
        </div>
      )}

      {keptInReviewed && (() => {
        // Only the owning USER is prompted to enter today's note. For a privileged viewer the card is read-only.
        const needNote = reviewDays >= 1 && !checkinDone && !isPrivilegedViewer;
        const refOnly = isPrivilegedViewer && reviewDays >= 1 && !checkinDone; // supervisor/admin viewing an outstanding note
        const accent = needNote ? "#DC2626" : (refOnly ? "#475569" : "#15803D");   // red = due; slate = reference; green = handled
        const soft = needNote ? "#FEF2F2" : (refOnly ? "#F8FAFC" : "#F0FDF4");
        const softBorder = needNote ? "#FECACA" : (refOnly ? "#E2E8F0" : "#BBF7D0");
        return (
          <div className="wv-note" style={{ flex: "0 0 auto", margin: "0 0 10px", background: "#fff", border: `1px solid ${softBorder}`, borderRadius: 10, boxShadow: "0 1px 2px rgba(15,23,42,.06)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, background: soft, padding: "6px 14px", borderBottom: `1px solid ${softBorder}`, borderTopLeftRadius: 9, borderTopRightRadius: 9 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 12.5, color: "#0f172a" }}>Review note<span style={{ fontWeight: 600, color: "#475569", fontSize: 11.5 }}> · {reviewDays === 0 ? "Kept in Reviewed today" : `In Reviewed ${reviewDays} day${reviewDays === 1 ? "" : "s"}`}{record.reason ? ` · ${record.reason}` : ""}</span></div>
              </div>
              <span style={{ flex: "0 0 auto", fontWeight: 800, fontSize: 11, color: "#fff", background: accent, borderRadius: 999, padding: "3px 11px", whiteSpace: "nowrap" }}>
                {needNote ? "Today's note needed" : (refOnly ? "Assignee's note" : "Handled today")}
              </span>
            </div>
            <div style={{ padding: "8px 14px" }}>
              {needNote ? (
                <div>
                  <label style={{ display: "block", fontWeight: 700, fontSize: 12.5, color: "#334155", marginBottom: 6 }}>Why is this still in Reviewed today?</label>
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start", maxWidth: 640 }}>
                    <textarea className="inp" rows={2} style={{ flex: 1, resize: "vertical" }} value={checkinComment}
                      onChange={(e) => { setCheckinComment(e.target.value); setCheckinErr(""); }}
                      placeholder="e.g. Awaiting MD notes / payor response — following up." />
                    <button className="btn btn-primary" disabled={checkinBusy} onClick={saveCheckin} style={{ whiteSpace: "nowrap", flex: "0 0 auto" }}>{checkinBusy ? <span className="spin" /> : "Save note"}</button>
                  </div>
                  {checkinErr && <div className="err-text" style={{ marginTop: 6, fontSize: 12.5 }}>{checkinErr}</div>}
                </div>
              ) : refOnly ? (
                <div style={{ fontSize: 13, color: "#334155", fontWeight: 600 }}>
                  Today&apos;s note is the assignee&apos;s to add — shown here for reference only.
                  {record.reviewLastComment && <span style={{ color: "#475569", fontWeight: 500 }}> Latest: {record.reviewLastComment}</span>}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#166534", fontWeight: 700 }}>
                  {reviewDays === 0 ? "Kept in Reviewed today — no note needed until tomorrow." : "Today's note added."}
                  {(checkinComment || record.reviewLastComment) && <span style={{ color: "#334155", fontWeight: 500 }}> — {checkinComment || record.reviewLastComment}</span>}
                </div>
              )}

              {(reviewHist && reviewHist.length > 0) && (
                // Earlier notes are shown on demand in an overlay popover (not an inline list) so they never
                // push the work form below the fold. The panel stays one compact row when collapsed.
                <div style={{ marginTop: 10, borderTop: "1px solid #eef2f7", paddingTop: 8, position: "relative" }}>
                  <button type="button" className="btn btn-ghost" style={{ fontSize: 11.5, padding: "3px 10px", fontWeight: 700, color: "#475569" }}
                    onClick={() => setRemarksOpen((o) => !o)}>
                    {remarksOpen ? "Hide earlier notes ▴" : `Earlier notes (${reviewHist.length}) ▾`}
                  </button>
                  {remarksOpen && (
                    <>
                      {/* click-away backdrop so the popover closes on an outside click */}
                      <div onClick={() => setRemarksOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 29 }} />
                      <div style={{ position: "absolute", zIndex: 30, top: "calc(100% + 6px)", left: 0, right: 0, maxHeight: "42vh", overflow: "auto", background: "#fff", border: "1px solid #e5e9f0", borderRadius: 10, boxShadow: "0 12px 32px rgba(15,23,42,.16)", padding: "6px 12px" }}>
                        {reviewHist.map((h, i) => (
                          <div key={i} style={{ display: "flex", gap: 12, padding: "7px 0", borderTop: i ? "1px dashed #eef2f7" : "none", fontSize: 12.5 }}>
                            <span style={{ flex: "0 0 88px", fontWeight: 700, color: "#334155" }}>{dayOnly(h.commentedAt || h.openedAt) || h.dayKey}</span>
                            <span style={{ flex: 1, color: "#0f172a", whiteSpace: "pre-wrap" }}>{h.comment}</span>
                            <span style={{ flex: "0 0 auto", color: "#94a3b8" }}>{h.userName || ""}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      <div className="wv-body wv2-body">
        <section className="wv-card wv2-form">
          <div className="wv2-left">
            {detailsCard}
            {returnPanel}
          </div>
          <div className="wv2-right">
          <h3 className="wv-h wv2-work-h">{isUploader ? "Payor decision" : "Your work"}</h3>
          <div className="wv-fields">
            {!isUploader ? (
              <>
                <CodeChips label="CPTs" codes={cpts} setCodes={setCpts} validate={(v) => CPT_RE.test(v)} placeholder="e.g. 78815" hint="Type a code and press Enter" onDirty={touch} />
                <CodeChips label="ICDs (optional)" codes={icds} setCodes={setIcds} validate={(v) => ICD_RE.test(v)} placeholder="e.g. C50.911" hint="Optional — type a code and press Enter" onDirty={touch} />

                {submittedViaField}
                {statusField}
                {methodFields}
                {statusExtraFields}
                {statusDateField}
                {helpRemarks}
              </>
            ) : (
              <>
                {statusField}
                {statusExtraFields}
                {statusDateField}
                {helpRemarks}
              </>
            )}
          </div>
          {error && <div className="err-text">{error}</div>}
          </div>
          <div className="wv-foot wv2-foot">
        <div className="spacer" />
        {!isUploader && !isReturn && !keptInReviewed && <button className="btn btn-addreq" onClick={() => setAddOpen(true)} disabled={busy}><Icon.plus width="15" height="15" /> Add another request</button>}
        {isUploader && !keptInReviewed && <button className="btn btn-ghost" onClick={sendToMod} disabled={busy} title="Send this record straight to Modification (stays in your Modification queue)"><Icon.history width="15" height="15" /> Send to modification</button>}
        {/* A record kept in Reviewed that is now being DECIDED (Approved/Denied/… — status no longer "Reviewed") must be
            Completed so the decision is recorded (shows in My work & Productivity) and routed; hide the plain Save & close
            for that case. Save & close stays for keeping it in Reviewed. */}
        {!(keptInReviewed && !isReviewed) && <button className="btn btn-ghost" onClick={saveAndClose} disabled={busy}>Save &amp; close</button>}
        {!isReviewed && <button className="btn btn-primary" onClick={complete} disabled={busy}>{busy ? <span className="spin" /> : "Complete"}</button>}
        {isReviewed && <span className="muted" style={{ fontSize: 12.5 }}>Reviewed stays in your list — use Save &amp; close.</span>}
          </div>
        </section>
      </div>

      {confirmComplete && (
        <Modal title="Complete this record?" sub="Please review your entries once more before completing."
          onClose={() => setConfirmComplete(false)}
          foot={<>
            <button className="btn btn-ghost" onClick={() => setConfirmComplete(false)}>Review again</button>
            <button className="btn btn-primary" onClick={proceedComplete} disabled={busy}>{busy ? <span className="spin" /> : (isSendBack ? "Yes, continue" : "Yes, complete")}</button>
          </>}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            {isSendBack
              ? <>You're about to send this record back for <b>{inboxToSend === "Modification Needed" ? "Modification" : "More Info"}</b>. Have you reviewed everything? You'll be asked whose issue it is next.</>
              : <>Have you reviewed all the details? Once you complete, this record moves on and leaves your list.</>}
          </p>
        </Modal>
      )}
      {issueReq && (
        <IssueModal
          title={`Send back for ${inboxToSend === "Modification Needed" ? "Modification" : "More Info"}`}
          sub="Pick whose issue caused this and why — it's stored so we can report client / patient / production issues."
          busy={issueBusy}
          onCancel={() => { setIssueReq(false); setForceMod(false); }}
          onConfirm={completeWithIssue} />
      )}
      {confirmClose && (
        <Modal title="Close without saving?" onClose={() => setConfirmClose(false)}
          foot={<><button className="btn btn-ghost" onClick={() => setConfirmClose(false)}>Keep working</button>
            <button className="btn btn-danger" onClick={() => { setConfirmClose(false); onClose(); }}>Close without saving</button></>}>
          <p style={{ margin: 0 }}>You've made changes that aren't saved. Close anyway?</p>
        </Modal>
      )}

      {addOpen && (
        <Modal title="Add another request" sub="Complete this request, then add another for the same patient" onClose={() => setAddOpen(false)}
          foot={<><button className="btn btn-ghost" onClick={() => setAddOpen(false)}>Cancel</button>
            <button className="btn btn-ghost" onClick={() => setAddOpen(false)}>Review once</button>
            <button className="btn btn-primary" disabled={busy} onClick={async () => { const ok = await doSave(true); if (ok) { setAddOpen(false); setAddForm(true); } }}>{busy ? <span className="spin" /> : "Complete"}</button></>}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>On <b>Complete</b>, this request is completed. Then keep the same patient (PID {record.mrn}) and just enter the next order name. Use <b>Review once</b> to check the fields first.</p>
        </Modal>
      )}

      {nogPrompt && (
        <Modal title="No guideline for this order"
          sub="Report it and you'll be able to complete this record."
          onClose={() => setNogPrompt(false)}
          foot={<>
            <button className="btn btn-ghost" onClick={() => setNogPrompt(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={nogBusy} onClick={submitNog}>{nogBusy ? <span className="spin" /> : "Submit & unlock"}</button>
          </>}>
          <div className="field req" style={{ marginBottom: 12 }}><label>Order name</label>
            <input className="inp" value={nogOrder} onChange={(e) => setNogOrder(e.target.value)} placeholder="Order name" /></div>
          <div className="field req"><label>Reason</label>
            <textarea className="inp" value={nogReason} onChange={(e) => setNogReason(e.target.value)} placeholder="What’s needed / why no guideline applies" rows={3} style={{ resize: "vertical" }} /></div>
        </Modal>
      )}

      {addForm && <AddRequestModal record={record} onClose={() => { setAddForm(false); onSaved(); }}
        onAdded={(rec) => { setAddForm(false); onOpenNew(rec); }} />}

      {GUIDELINES_ENABLED && showGuidelines && <GuidelinesModal record={record} initialCode={viewCode} startSearch={searchOpen} onClose={() => { setShowGuidelines(false); setViewCode(null); setSearchOpen(false); }}
        onUsed={(code) => {
          if (!code) return;
          setGlCodes((cs) => (cs.includes(code) ? cs : [...cs, code]));
          copiedThisOpen.current = true;
          // (No auto-attach on copy — the worker must paste the copied code into the Guideline ID box, same as Submission.)
        }}
        onNoGuideline={() => setNoGuideline(true)} />}
    </div>
  );
}

function AddRequestModal({ record, onClose, onAdded }) {
  const toast = useToast();
  const [orderName, setOrderName] = useState("");
  const [names, setNames] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [dupe, setDupe] = useState(null);       // { exists, message } when this order already exists
  const [checking, setChecking] = useState(false);
  useEffect(() => { api.orderNames().then((n) => setNames(n || [])).catch(() => setNames([])); }, []);

  // Pre-check the MRN + order against inventory as the user settles on a name.
  useEffect(() => {
    setDupe(null); setErr("");
    const on = orderName.trim();
    if (!on) { setChecking(false); return; }
    let alive = true; setChecking(true);
    const t = setTimeout(async () => {
      try { const r = await api.addRequestCheck(record.id, on); if (alive) setDupe(r && r.exists ? r : null); }
      catch { /* ignore; submit will still guard */ }
      finally { if (alive) setChecking(false); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [orderName, record.id]);

  const submit = async () => {
    setErr("");
    if (!orderName.trim()) return setErr("Order name is required.");
    if (dupe && dupe.exists) return;   // blocked
    setBusy(true);
    try { const rec = await api.addRequest(record.id, { orderName: orderName.trim() }); toast("New request added", "ok"); onAdded(rec); }
    catch (e) {
      if (/already present|already exists|assigned to/i.test(e.message || "")) setDupe({ exists: true, message: e.message });
      else setErr(e.message);
    } finally { setBusy(false); }
  };

  const blocked = !!(dupe && dupe.exists);
  return (
    <Modal title="Add another request" sub={`Same patient (PID ${record.mrn}), new order`} onClose={onClose}
      foot={blocked
        ? <button className="btn btn-ghost" onClick={onClose}>Close</button>
        : <><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={submit} disabled={busy || checking || !orderName.trim()}>{busy ? <span className="spin" /> : "Add & open"}</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>Same patient — just enter the next order name. The unique ID is formed automatically (same dates), and we check it isn't already in the inventory.</p>
      <div className="field"><label>Order name</label>
        <input className="inp" list="ar-order-names" value={orderName} onChange={(e) => setOrderName(e.target.value)} placeholder="Search or type an order name" />
        <datalist id="ar-order-names">{names.map((n) => <option key={n} value={n} />)}</datalist>
      </div>
      {checking && <div className="hint" style={{ marginTop: 4 }}>Checking inventory…</div>}
      {blocked && <div className="err-text" style={{ marginTop: 8 }}>{dupe.message}</div>}
      {err && <div className="err-text">{err}</div>}
    </Modal>
  );
}

const STAGE_LBL = { SUBMISSION: "Submission", END_PROCESS: "End Process", MODIFICATION: "Modification", MORE_INFO: "More Info" };
const _pad = (n) => String(n).padStart(2, "0");
const dayOnly = (t) => { try { if (!t) return "—"; const d = new Date(t); return `${_pad(d.getMonth() + 1)}/${_pad(d.getDate())}/${d.getFullYear()}`; } catch { return "—"; } };

function HRow({ label, value, color }) {
  if (!value) return null;
  return <div className="hrow"><span className="hrow-l">{label}</span><span className="hrow-v" style={{ whiteSpace: "pre-wrap", ...(color ? { color, fontWeight: 700 } : {}) }}>{value}</span></div>;
}

function WorkHistory({ raw, record }) {
  let list = [];
  try { list = raw ? JSON.parse(raw) : []; } catch { list = []; }

  // fallback for records worked before history tracking existed
  if (!Array.isArray(list) || !list.length) {
    if (!record) return null;
    list = [{
      by: record.workedByName, date: record.workedAt ? dayOnly(record.workedAt) : null,
      stage: record.sheet, status: record.paStatus, inbox: record.inboxValue,
      cpts: record.submittedCpts, icds: record.submittedIcds, reason: record.reason || record.uploaderNote, remarks: record.remarks || record.uploaderRemarks,
    }];
  }
  const rows = [...list].reverse(); // newest first
  return (
    <div className="wv-history">
      {rows.map((h, i) => (
        <div className="htl" key={i}>
          <div className="htl-mark"><span className="htl-dot" />{i < rows.length - 1 && <span className="htl-line" />}</div>
          <div className="htl-card">
            <div className="htl-head">
              <b>{h.by || "A user"}</b>
              <span className="htl-when">{h.date || dayOnly(h.dateISO)}</span>
              {h.stage && <span className="htl-stage">{STAGE_LBL[h.stage] || h.stage}</span>}
            </div>
            <div className="htl-body">
              <HRow label="Status" value={h.status} color="#1d4ed8" />
              <HRow label="Inbox" value={h.inbox} />
              <HRow label="CPTs" value={h.cpts} />
              <HRow label="ICDs" value={h.icds} />
              <HRow label="Reason" value={h.reason} />
              <HRow label="Remarks" value={h.remarks} />
              {h.issueType && <HRow label="Issue" value={`${h.issueType} issue`} color="#b91c1c" />}
              <HRow label="Issue reason" value={h.issueReason} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}